<script setup lang="ts">
/**
 * Singbac Incorporation (Pre) — 3종 문서를 한 화면에 연속 표시.
 * 회사 정보를 한 번 채우면 여러 문서에 공유(필드 키 공유).
 *
 * 업무별 3단계(Stage):
 *  · Stage 1 · Company     : Draft Constitution (회사 1부)
 *  · Stage 2 · Officers    : Form 45(이사별 dir_{id}/) · Form 45B(비서별 sec_{id}/)
 *  · Stage 3 · Controllers : Notice to Controller (registrable controller별 ctrl_{id}/)
 * → 각 단계 상단 "+ 추가" 버튼으로 이사/비서/지배자 부수를 늘린다.
 */
import { onMounted, ref } from 'vue';
import DocScope from './cdd/DocScope.vue';
import SingbacConstitutionDoc from './SingbacConstitutionDoc.vue';
import SingbacForm45BConsentDoc from './SingbacForm45BConsentDoc.vue';
import SingbacForm45DirectorConsentDoc from './SingbacForm45DirectorConsentDoc.vue';
import SingbacNoticeControllerDoc from './SingbacNoticeControllerDoc.vue';

interface StageDef { no: number; en: string; short: string }
const STAGES: StageDef[] = [
  { no: 1, en: 'Company (Constitution)', short: 'Company' },
  { no: 2, en: 'Officers (Directors & Secretary)', short: 'Officers' },
  { no: 3, en: 'Registrable Controllers (s.386AG)', short: 'Controllers' },
];
const activeStage = ref(1);

/** Constitution 자식 — subscriber 행 재설정(회사 변경 시)용. */
const constitutionRef = ref<InstanceType<typeof SingbacConstitutionDoc> | null>(null);

/** registrable controller 부수 id 목록 (Notice to Controller). */
const controllerIds = ref<number[]>([1]);
let seq = 1;
function addController() {
  controllerIds.value.push(++seq);
}
function removeController(id: number) {
  if (controllerIds.value.length <= 1) return;
  controllerIds.value = controllerIds.value.filter((x) => x !== id);
}

/** 비서 부수 id 목록 (Form 45B — 비서 여러 명 가능). */
const secretaryIds = ref<number[]>([1]);
let secSeq = 1;
function addSecretary() {
  secretaryIds.value.push(++secSeq);
}
function removeSecretary(id: number) {
  if (secretaryIds.value.length <= 1) return;
  secretaryIds.value = secretaryIds.value.filter((x) => x !== id);
}

/** 이사 부수 id 목록 (Form 45 — 이사별 1부). */
const directorIds = ref<number[]>([1]);
let dirSeq = 1;
function addDirector() {
  directorIds.value.push(++dirSeq);
}
function removeDirector(id: number) {
  if (directorIds.value.length <= 1) return;
  directorIds.value = directorIds.value.filter((x) => x !== id);
}

// ─── 문서 확대 (시니어 가독성) — localStorage 유지 ───
const ZOOM_KEY = 'singbacPre.docZoom';
const ZOOM_MIN = 100;
const ZOOM_MAX = 175;
const ZOOM_STEP = 25;
function clampZoom(n: number) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP));
}
function readStoredZoom(): number {
  try {
    const n = Number(localStorage.getItem(ZOOM_KEY));
    if (Number.isFinite(n)) return clampZoom(n);
  } catch {
    /* ignore */
  }
  return 100;
}
const docZoom = ref(readStoredZoom());
function setDocZoom(n: number) {
  docZoom.value = clampZoom(n);
  try {
    localStorage.setItem(ZOOM_KEY, String(docZoom.value));
  } catch {
    /* ignore */
  }
}

type LocalState = {
  controllerIds?: number[];
  secretaryIds?: number[];
  directorIds?: number[];
  subscriberIds?: number[];
  activeStage?: number;
};
const pendingLocal = ref<LocalState | null>(null);
let mounted = false;

function applyLocalState(state: LocalState) {
  if (!state) return;
  if (Array.isArray(state.controllerIds) && state.controllerIds.length) {
    controllerIds.value = [...state.controllerIds];
    seq = Math.max(...state.controllerIds, seq);
  }
  if (Array.isArray(state.secretaryIds) && state.secretaryIds.length) {
    secretaryIds.value = [...state.secretaryIds];
    secSeq = Math.max(...state.secretaryIds, secSeq);
  }
  if (Array.isArray(state.directorIds) && state.directorIds.length) {
    directorIds.value = [...state.directorIds];
    dirSeq = Math.max(...state.directorIds, dirSeq);
  }
  // Constitution subscriber 행 — 회사 변경 시 정확한 개수로 재설정(자동감지 merge 로는 축소 불가).
  if (Array.isArray(state.subscriberIds) && state.subscriberIds.length) {
    constitutionRef.value?.setSubscriberRows(state.subscriberIds);
  }
  if (state.activeStage && state.activeStage >= 1 && state.activeStage <= STAGES.length) {
    activeStage.value = state.activeStage;
  }
}
function gatherLocalState(): LocalState {
  return {
    controllerIds: [...controllerIds.value],
    secretaryIds: [...secretaryIds.value],
    directorIds: [...directorIds.value],
    activeStage: activeStage.value,
  };
}
function restoreLocalState(state: LocalState) {
  if (!state) return;
  pendingLocal.value = state;
  if (mounted) applyLocalState(state);
}

onMounted(() => {
  mounted = true;
  if (pendingLocal.value) applyLocalState(pendingLocal.value);
});

defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="allot-pkg singbac-pkg">
    <!-- Stage 스텝퍼 -->
    <div class="stepper">
      <button
        v-for="st in STAGES"
        :key="st.no"
        type="button"
        class="step"
        :class="{ active: activeStage === st.no, done: activeStage > st.no }"
        :title="`Stage ${st.no} · ${st.en}`"
        @click="activeStage = st.no"
      >
        <span class="step-no">{{ activeStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.short }}</small></span>
      </button>
    </div>

    <div class="zoom-bar">
      <div class="doc-zoom" role="group" aria-label="Larger text">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" title="Smaller text" @click="setDocZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" title="Reset size" @click="setDocZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" title="Larger text" @click="setDocZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>

    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <!-- Stage 1 · Company (Constitution) -->
      <section v-show="activeStage === 1" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 1</span><span class="stage-t">Company (Constitution)</span></div>

        <div class="grp-lbl">Draft Constitution — one per company</div>
        <div class="pkg-doc">
          <div class="pkg-doclabel"><span class="ic">📜</span><span class="nm">Draft Constitution</span></div>
          <div class="simple-frame"><SingbacConstitutionDoc ref="constitutionRef" /></div>
        </div>
      </section>

      <!-- Stage 2 · Officers (Directors & Secretary) -->
      <section v-show="activeStage === 2" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 2</span><span class="stage-t">Officers (Directors &amp; Secretary)</span></div>

        <!-- 최상단 문서 추가 -->
        <div class="sg-toolbar add-bar">
          <span class="add-lbl">Add document</span>
          <button type="button" class="add-btn" @click="addDirector">+ Form 45 (Director)</button>
          <button type="button" class="add-btn" @click="addSecretary">+ Form 45B (Secretary)</button>
        </div>

        <!-- Form 45 — 이사 명수대로 -->
        <div class="grp-lbl">Form 45 (Consent to Act as Director) — one per director</div>
        <template v-for="(id, i) in directorIds" :key="`dir-${id}`">
          <div class="pkg-doc">
            <div class="pkg-doclabel">
              <span class="ic">🖋️</span><span class="nm">Form 45 — Consent to Act as Director</span>
              <span class="copy">Director #{{ i + 1 }}</span>
              <button v-if="directorIds.length > 1" type="button" class="tb-rm" title="Remove director" @click="removeDirector(id)">×</button>
            </div>
            <div class="simple-frame">
              <DocScope :scope="`dir_${id}/`"><SingbacForm45DirectorConsentDoc /></DocScope>
            </div>
          </div>
        </template>

        <!-- Form 45B — 비서 명수대로 -->
        <div class="grp-lbl">Form 45B (Consent to Act as Secretary) — one per secretary</div>
        <template v-for="(id, i) in secretaryIds" :key="`sec-${id}`">
          <div class="pkg-doc">
            <div class="pkg-doclabel">
              <span class="ic">✍️</span><span class="nm">Form 45B — Consent to Act as Secretary</span>
              <span class="copy">Secretary #{{ i + 1 }}</span>
              <button v-if="secretaryIds.length > 1" type="button" class="tb-rm" title="Remove secretary" @click="removeSecretary(id)">×</button>
            </div>
            <div class="simple-frame">
              <DocScope :scope="`sec_${id}/`"><SingbacForm45BConsentDoc /></DocScope>
            </div>
          </div>
        </template>
      </section>

      <!-- Stage 3 · Registrable Controllers -->
      <section v-show="activeStage === 3" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 3</span><span class="stage-t">Registrable Controllers (s.386AG)</span></div>

        <!-- 최상단 문서 추가 -->
        <div class="sg-toolbar add-bar">
          <span class="add-lbl">Add document</span>
          <button type="button" class="add-btn" @click="addController">+ Controller notice</button>
        </div>

        <!-- Notice to Controller — registrable controller 명수대로 -->
        <div class="grp-lbl">Notice to Controller — one per registrable controller</div>
        <template v-for="(id, i) in controllerIds" :key="`ctrl-${id}`">
          <div class="pkg-doc">
            <div class="pkg-doclabel">
              <span class="ic">📄</span><span class="nm">Notice to Controller (s.386AG(2)(a))</span>
              <span class="copy">Controller #{{ i + 1 }}</span>
              <button v-if="controllerIds.length > 1" type="button" class="tb-rm" title="Remove controller" @click="removeController(id)">×</button>
            </div>
            <div class="simple-frame">
              <DocScope :scope="`ctrl_${id}/`"><SingbacNoticeControllerDoc /></DocScope>
            </div>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<style scoped>
.allot-pkg { color: #111; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 14px; box-sizing: border-box; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; line-height: 1; }
.doc-zoom-btn { min-width: 36px; height: 32px; font-size: 15px; }
.doc-zoom-btn:hover:not(:disabled) { background: #fff; color: #191f28; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; height: 32px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 10px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; letter-spacing: 0.01em; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; }
.tb-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; line-height: 1; padding: 0 2px; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20, 30, 60, 0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }

/* Stage 스텝퍼 */
.stepper { display: flex; flex-wrap: nowrap; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20, 30, 60, 0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; transition: all 0.15s; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; }
.step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.stage { width: 100%; max-width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; letter-spacing: 0.02em; }
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }

.sg-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.grp-lbl { width: 100%; margin: 0 0 12px; padding: 2px 2px 8px; border-bottom: 1px solid #eef1f6; font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: 0.01em; }

/* 최상단 문서 추가 바 */
.add-bar { margin: 0 0 18px; padding: 10px 14px; border: 1px solid #e6ebf3; border-radius: 12px; background: #fbfcff; box-shadow: none; gap: 8px; }
.add-bar .add-lbl { font-size: 11.5px; font-weight: 800; color: #8a94a6; margin-right: auto; }
.add-bar .add-btn { border: 1px solid #d5e2ff; background: #fff; color: #1b6fe0; border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 700; cursor: pointer; }
.add-bar .add-btn:hover { background: #eef4ff; border-color: #b9d0ff; }
</style>

<style>
.paper:has(.singbac-pkg) {
  width: 794px !important;
  max-width: 100% !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
.singbac-pkg .allot-doc { width: 100%; max-width: 100%; box-sizing: border-box; }
.singbac-pkg .cdd-tbl { width: 100% !important; table-layout: fixed !important; box-sizing: border-box; }
.singbac-pkg .cdd-tbl td, .singbac-pkg .cdd-tbl th { box-sizing: border-box; overflow-wrap: break-word; word-break: normal; vertical-align: top; }
.singbac-pkg .lv-field { display: inline-flex !important; max-width: 100%; box-sizing: border-box; white-space: normal !important; flex-wrap: wrap; align-items: baseline; vertical-align: baseline; }
.singbac-pkg .lv-field .lv-text { min-width: 0; overflow-wrap: break-word; word-break: break-word; }
</style>
